/**
 * Teams State
 *
 * NGXS state for managing team data.
 */

import { Injectable } from '@angular/core';
import { State, Action, StateContext, Selector, NgxsOnInit, Store } from '@ngxs/store';
import { Partner } from 'src/app/models/schema/Partner.model';
import {
  LoadTeams,
  LoadTeamsSuccess,
  LoadTeamsFailure,
  AddTeam,
  UpdateTeam,
  RemoveTeam,
  ClearTeams,
  SetTeamsLoading,
  ClearTeamsError,
  SetTeamProfile,
} from './teams.actions';
import { AuthState } from 'src/app/store/auth';
import { distinctUntilChanged, filter } from 'rxjs/operators';

/**
 * Team Model
 * 
 * A Team is an alias for a Partner object.
 * Teams represent partner organizations that users can be members of.
 */
export type Team = Partner;

/**
 * Teams State Model
 */
export interface TeamsStateModel {
  /** Map of team ID to team data */
  teams: { [id: string]: Team };
  /** Array of team IDs for ordering */
  teamIds: string[];
  /** Loading state */
  loading: boolean;
  /** Whether teams have been loaded */
  loaded: boolean;
  /** Error message if any */
  error: string | null;
}

/**
 * Default state
 */
const defaults: TeamsStateModel = {
  teams: {},
  teamIds: [],
  loading: false,
  loaded: false,
  error: null,
};

/**
 * Teams State
 */
@State<TeamsStateModel>({
  name: 'teams',
  defaults,
})
@Injectable()
export class TeamsState implements NgxsOnInit {
  constructor(private store: Store) {}

  /**
   * Initialize state listeners
   * 
   * Listens for auth state changes and clears state if user is not authenticated.
   * This handles all cases where user becomes unauthenticated (sign out, account deletion, etc.)
   */
  ngxsOnInit(ctx: StateContext<TeamsStateModel>): void {
    let previousAuthState: boolean | null = null;

    // Subscribe to auth state changes
    this.store.select(AuthState.isAuthenticated).pipe(
      distinctUntilChanged() // Only emit when authentication status actually changes
    ).subscribe((isAuthenticated) => {
      // Only clear if we're transitioning from authenticated to unauthenticated
      // (not on initial load where auth state might not be hydrated yet)
      if (previousAuthState === true && !isAuthenticated) {
        // Reset state
        ctx.setState(defaults);
        
        // Clear the locally stored teams from localStorage
        try {
          localStorage.removeItem('@@STATE.teams');
          console.log('[TeamsState] Cleared teams from localStorage');
        } catch (error) {
          console.warn('[TeamsState] Failed to clear teams from localStorage:', error);
        }
        
        console.log('[TeamsState] State cleared - user signed out');
      }

      // Track previous auth state
      previousAuthState = isAuthenticated;
    });
  }

  /**
   * Get the entire teams state
   */
  @Selector()
  static getState(state: TeamsStateModel): TeamsStateModel {
    return state;
  }

  /**
   * Get all teams as an array
   */
  @Selector()
  static teams(state: TeamsStateModel): Team[] {
    return state.teamIds.map((id) => state.teams[id]).filter(Boolean);
  }

  /**
   * Get a team by ID
   */
  static team(teamId: string) {
    return (state: TeamsStateModel): Team | null => {
      return state.teams[teamId] || null;
    };
  }

  /**
   * Get team IDs
   */
  @Selector()
  static teamIds(state: TeamsStateModel): string[] {
    return state.teamIds;
  }

  /**
   * Get team names
   */
  @Selector()
  static teamNames(state: TeamsStateModel): string[] {
    return state.teamIds
      .map((id) => state.teams[id]?.name)
      .filter(Boolean) as string[];
  }

  /**
   * Get loading state
   */
  @Selector()
  static loading(state: TeamsStateModel): boolean {
    return state.loading;
  }

  /**
   * Get loaded state
   */
  @Selector()
  static loaded(state: TeamsStateModel): boolean {
    return state.loaded;
  }

  /**
   * Get error state
   */
  @Selector()
  static error(state: TeamsStateModel): string | null {
    return state.error;
  }

  /**
   * Check if user has teams
   */
  @Selector()
  static hasTeams(state: TeamsStateModel): boolean {
    return state.teamIds.length > 0;
  }

  /**
   * Get team count
   */
  @Selector()
  static teamCount(state: TeamsStateModel): number {
    return state.teamIds.length;
  }

  /**
   * Get a team's profile by team ID
   */
  static teamProfile(teamId: string) {
    return (state: TeamsStateModel): any | null => {
      return state.teams[teamId]?._profile || null;
    };
  }

  /**
   * Load teams
   */
  @Action(LoadTeams)
  loadTeams(ctx: StateContext<TeamsStateModel>, action: LoadTeams) {
    ctx.patchState({
      loading: true,
      error: null,
    });
  }

  /**
   * Load teams success
   */
  @Action(LoadTeamsSuccess)
  loadTeamsSuccess(ctx: StateContext<TeamsStateModel>, action: LoadTeamsSuccess) {
    const teams: { [id: string]: Team } = {};
    const teamIds: string[] = [];

    action.teams.forEach((team) => {
      if (team.id) {
        teams[team.id] = team;
        teamIds.push(team.id);
      }
    });

    ctx.patchState({
      teams,
      teamIds,
      loading: false,
      loaded: true,
      error: null,
    });
  }

  /**
   * Load teams failure
   */
  @Action(LoadTeamsFailure)
  loadTeamsFailure(ctx: StateContext<TeamsStateModel>, action: LoadTeamsFailure) {
    ctx.patchState({
      loading: false,
      error: action.error,
    });
  }

  /**
   * Add a team
   */
  @Action(AddTeam)
  addTeam(ctx: StateContext<TeamsStateModel>, action: AddTeam) {
    if (!action.team.id) return; // Guard against missing ID
    
    const state = ctx.getState();
    const teams = { ...state.teams, [action.team.id]: action.team };
    const teamIds = [...state.teamIds];
    
    if (!teamIds.includes(action.team.id)) {
      teamIds.push(action.team.id);
    }

    ctx.patchState({
      teams,
      teamIds,
    });
  }

  /**
   * Update a team
   */
  @Action(UpdateTeam)
  updateTeam(ctx: StateContext<TeamsStateModel>, action: UpdateTeam) {
    const state = ctx.getState();
    const existingTeam = state.teams[action.teamId];

    if (!existingTeam) {
      return;
    }

    const updatedTeam = { ...existingTeam, ...action.updates };
    const teams = { ...state.teams, [action.teamId]: updatedTeam };

    ctx.patchState({ teams });
  }

  /**
   * Remove a team
   */
  @Action(RemoveTeam)
  removeTeam(ctx: StateContext<TeamsStateModel>, action: RemoveTeam) {
    const state = ctx.getState();
    const teams = { ...state.teams };
    delete teams[action.teamId];

    const teamIds = state.teamIds.filter((id) => id !== action.teamId);

    ctx.patchState({
      teams,
      teamIds,
    });
  }

  /**
   * Clear teams
   */
  @Action(ClearTeams)
  clearTeams(ctx: StateContext<TeamsStateModel>) {
    ctx.setState(defaults);
  }

  /**
   * Set loading state
   */
  @Action(SetTeamsLoading)
  setLoading(ctx: StateContext<TeamsStateModel>, action: SetTeamsLoading) {
    ctx.patchState({
      loading: action.loading,
    });
  }

  /**
   * Clear error
   */
  @Action(ClearTeamsError)
  clearError(ctx: StateContext<TeamsStateModel>) {
    ctx.patchState({
      error: null,
    });
  }

  /**
   * Set team profile
   * 
   * Sets the user's profile for a specific team (local only, not persisted)
   */
  @Action(SetTeamProfile)
  setTeamProfile(ctx: StateContext<TeamsStateModel>, action: SetTeamProfile) {
    const state = ctx.getState();
    const existingTeam = state.teams[action.teamId];

    if (!existingTeam) {
      return;
    }

    const updatedTeam = { ...existingTeam, _profile: action.profile };
    const teams = { ...state.teams, [action.teamId]: updatedTeam };

    ctx.patchState({ teams });
  }
}

